import React, { memo, useState, useMemo } from "react";
import { isEmpty } from "@swiggy-private/js-utils";
import { Image } from "@swiggy-private/dls-web-core/dist/components/Image";
import { SuperButton } from "@swiggy-private/dls-web-core/dist/components/SuperButton";
import { ISuperButtonType } from "@swiggy-private/dls-web-core/dist/components/SuperButton/types";
import { IMAGES } from "./constant";
import {
    getOffersToShow,
    getRemainingOffersCount,
    parseTitleSegments,
    shouldRenderMiddleSegment,
} from "./helpers";
import {
    PaymentCard,
    CardHeader,
    IconContainer,
    CardTitleContainer,
    PriceText,
    OffersContainer,
    OfferItem,
    OfferText,
    MoreOffersText,
    ButtonContainer,
    DisclaimerMessage,
    DotIcon,
} from "./styled";
import { IPaymentOption } from "./types";

interface IPaymentOptionCardProps {
    option: IPaymentOption;
    onButtonClick: () => void;
}

const PaymentOptionCard = memo(({ option, onButtonClick }: IPaymentOptionCardProps) => {
    const [isExpanded, setIsExpanded] = useState(false);

    // Determine which offers to show
    const offersToShow = useMemo(
        () => getOffersToShow(option.offers, option.shownOfferLimit, isExpanded),
        [option.offers, option.shownOfferLimit, isExpanded]
    );

    // Calculate remaining offers count
    const remainingOffersCount = useMemo(
        () => getRemainingOffersCount(option.offers, option.shownOfferLimit, isExpanded),
        [option.offers, option.shownOfferLimit, isExpanded]
    );

    const handleMoreOffersClick = () => {
        setIsExpanded(true);
    };

    const renderTitle = () => {
        if (isEmpty(option.strikeThroughAmount) || isEmpty(option.finalAmount)) {
            return <PriceText variant="V2.body1.neutral.bold">{option.title.trim()}</PriceText>;
        }

        const { beforeStrike, middle, afterFinalAmount } = parseTitleSegments(option.title);

        return (
            <>
                <PriceText variant="V2.body1.neutral.bold">{beforeStrike}</PriceText>
                <PriceText variant="V2.body1.neutral.regular" isStrikethrough>
                    {option.strikeThroughAmount.trim()}
                </PriceText>
                {shouldRenderMiddleSegment(middle) && (
                    <PriceText variant="V2.body1.neutral.bold">{middle}</PriceText>
                )}
                <PriceText variant="V2.body1.neutral.bold" isGreen>
                    {option.finalAmount.trim()}
                </PriceText>
                <PriceText variant="V2.body1.neutral.bold">{afterFinalAmount}</PriceText>
            </>
        );
    };

    return (
        <PaymentCard>
            <CardHeader>
                {!isEmpty(option.icon) && (
                    <IconContainer>
                        <Image
                            cloudinaryId={option.icon}
                            alt={IMAGES.ALT_TEXT}
                            cloudinaryHeight={IMAGES.ICON_SIZE * 2}
                            cloudinaryWidth={IMAGES.ICON_SIZE * 2}
                            height={String(IMAGES.ICON_SIZE)}
                            width={String(IMAGES.ICON_SIZE)}
                        />
                    </IconContainer>
                )}
                <CardTitleContainer>
                    <div>{renderTitle()}</div>
                </CardTitleContainer>
            </CardHeader>

            {offersToShow && offersToShow.length > 0 && (
                <OffersContainer>
                    {offersToShow.map((offer, index) => (
                        <OfferItem key={index}>
                            {!isEmpty(offer.icon) ? (
                                <Image
                                    cloudinaryId={offer.icon}
                                    src={offer.icon}
                                    alt={IMAGES.CHECKMARK_ALT_TEXT}
                                    cloudinaryHeight={IMAGES.CHECKMARK_ICON_SIZE * 2}
                                    cloudinaryWidth={IMAGES.CHECKMARK_ICON_SIZE * 2}
                                    height={String(IMAGES.CHECKMARK_ICON_SIZE)}
                                    width={String(IMAGES.CHECKMARK_ICON_SIZE)}
                                />
                            ) : (
                                <DotIcon />
                            )}
                            <OfferText
                                variant="V2.overline.neutral.medium"
                                colorToken={offer.colorToken}
                            >
                                {offer.text}
                            </OfferText>
                        </OfferItem>
                    ))}
                    {remainingOffersCount > 0 && (
                        <MoreOffersText
                            variant="V2.overline.neutral.medium"
                            onClick={handleMoreOffersClick}
                        >
                            +{remainingOffersCount} more
                        </MoreOffersText>
                    )}
                </OffersContainer>
            )}

            <ButtonContainer>
                <SuperButton
                    buttonType={option.cta.type.toLowerCase() as ISuperButtonType}
                    buttonSize="medium"
                    onClick={onButtonClick}
                >
                    {option.cta.text}
                </SuperButton>
            </ButtonContainer>

            {option.ctaMessage && (
                <DisclaimerMessage variant="V2.overline.neutral.medium">
                    {option.ctaMessage}
                </DisclaimerMessage>
            )}
        </PaymentCard>
    );
});

PaymentOptionCard.displayName = "PaymentOptionCard";

export default PaymentOptionCard;
